import React from 'react';
import * as d3 from 'd3';

class Scatterplot extends React.Component {
  componentDidMount() {
    this.renderD3();
  }
  componentDidUpdate() {
    // Remove d3 dom between updates and reload with new data
    let scatterplot = d3.select('#scatterplot');
    // Remove all child divs
    scatterplot.selectAll('div').remove();
    this.renderD3();
  }
  //Regression Line Equation (least squares equation)
  leastSquaresEquation = (xAxisData, yAxisData) => {
    let reduceAddition = function (prev, cur) {
      return prev + cur;
    }
    let xBar = xAxisData.reduce(reduceAddition) * 1.0 / xAxisData.length;
    let yBar = yAxisData.reduce(reduceAddition) * 1.0 / yAxisData.length;
    let ssXX = xAxisData.map(function (d) {
      return Math.pow(d - xBar, 2);
    }).reduce(reduceAddition);
    let ssYY = yAxisData.map(function (d) {
      return Math.pow(d - yBar, 2);
    }).reduce(reduceAddition);
    let ssXY = xAxisData.map(function (d, i) {
      return (d - xBar) * (yAxisData[i] - yBar);
    }).reduce(reduceAddition);
    let slope = ssXY / ssXX;
    let intercept = yBar - (xBar * slope);
    let rSquare = Math.pow(ssXY, 2) / (ssXX * ssYY);
    return [slope, intercept, rSquare, function (x) { return x * slope + intercept }];
  }
  //Create multiple points along the regression line to plot a regression line
  randomFloatBetween = (minValue, maxValue, precision) => {
    if (typeof (precision) == 'undefined') {
      precision = 2;
    }
    return parseFloat(Math.min(minValue + (Math.random() * (maxValue - minValue)), maxValue).toFixed(precision));
  }
  calculateLineData = (leastSquares, xRange, iterations) => {
    let returnData = [];
    for (let i = 0; i < iterations; i++) {
      let randomX = this.randomFloatBetween(xRange[0], xRange[1]);
      returnData.push({
        xVal: randomX,
        yVal: (randomX * leastSquares[0] + leastSquares[1])
      })
    }
    return returnData;
  }
  //Render D3, all rendering on D3's DOM.
  renderD3 = () => {
    const mapTypePriceLabel = this.props.selectedCompDataType === 'rent' ? 'Rent Price' :
      this.props.selectedCompDataType === 'sale' ? 'Sale Price' :
        this.props.selectedCompDataType === 'sold' ? 'Sold Price' :
          this.props.selectedCompDataType === 'owned' ? 'Owned Price' : 'Price';
    let height = this.props.height - this.props.margin.marginBottom - this.props.margin.marginTop;
    let width = this.props.width - this.props.margin.marginLeft - this.props.margin.marginRight;
    let x = d3.scaleLinear().range([0, width]);
    let y = d3.scaleLinear().range([height, 0]);
    x.domain(d3.extent(this.props.scatterData, this.props.selectX)).nice();
    y.domain(d3.extent(this.props.scatterData, this.props.selectY)).nice();
    const xAxis = d3.axisBottom().scale(x);
    const yAxis = d3.axisLeft().scale(y).ticks(5);
    const circlePoints = this.props.scatterData.map(datum => {
      return ({
        ...datum,
        x: x(this.props.selectX(datum)),
        y: y(this.props.selectY(datum))
      })
    });
    let xAxisData = this.props.scatterData.map(this.props.selectX);
    let yAxisData = this.props.scatterData.map(this.props.selectY);
    let leastSquareCoeff = this.leastSquaresEquation(xAxisData, yAxisData);
    let lineData = this.calculateLineData(leastSquareCoeff, d3.extent(xAxisData), 200);
    let lineGenerator = d3.line().x(function (d) { return x(d.xVal); }).y(function (d) { return y(d.yVal); });
    d3.select("#scatterplot").style("position", "relative").append("div").attr("id", "body");
    let svg = d3.select("#body").append("svg").attr("class", "container").attr("height", this.props.height).attr("width", this.props.width);
    // Check if there are more than 1 point, else the leastSquareCoeff and lineData are messed up
    if (xAxisData.length > 1) {
      svg.append("text").attr("id", "equation").attr("y", "1rem").style("font-size", "0.75rem").style("font-family", "Roboto")
        .text((leastSquareCoeff[1] < 0 ?
          `Regression: y=${leastSquareCoeff[0].toFixed(2)}x${leastSquareCoeff[1].toFixed(2)} rSquared: ${leastSquareCoeff[2].toFixed(2)}`
          :
          `Regression: y=${leastSquareCoeff[0].toFixed(2)}x+${leastSquareCoeff[1].toFixed(2)} rSquared: ${leastSquareCoeff[2].toFixed(2)}`)
        );
    }
    svg.append("text").attr("id", "xAxisLabel").attr("x", (width) / 2).attr("y", height + this.props.margin.marginBottom).style("font-family", "Roboto").style("font-weight", "bold").style('font-size', '16px').text("Living Area (sqft)");
    svg.append("text").attr("id", "yAxisLabel").attr("transform", "rotate(-90)").attr("dx", "-14em").attr("dy", "1em").style("font-family", "Roboto").style("font-weight", "bold").style('font-size', '16px').text(`${mapTypePriceLabel} ($)`);
    let gBody = svg.append("g").attr("transform", `translate(${this.props.margin.marginLeft + 5},${this.props.margin.marginTop})`).attr("class", "d3Body");
    //Grid lines
    function make_x_gridlines() {
      return d3.axisBottom(x).ticks(5);
    }
    function make_y_gridlines() {
      return d3.axisLeft(y).ticks(5);
    }
    gBody.append("g").attr("class", "grid").attr("transform", `translate(0,${height})`).call(make_x_gridlines().tickSize(-height).tickFormat(""));
    gBody.append("g").attr("class", "grid").call(make_y_gridlines().tickSize(-width).tickFormat(""));
    //Axis lines
    gBody.append("g").attr("class", "xAxis").call(xAxis).attr("transform", `translate(0,${height})`).style("font-family", "Roboto");
    gBody.append("g").attr("class", "yAxis").call(yAxis).attr("transform", "translate(0,0)").style("font-family", "Roboto");
    let gScatter = gBody.append("g").attr("class", "scatter").attr("width", width).attr("height", height);

    //Tooltip
    d3.select("#scatterplot").append("div").attr("class", "tooltip").style("visibility", "hidden").style('font-size', '14px').style("font-family", "Roboto").text('Pricevsqft');
    // Shape Legend
    let shapeLegend = d3.select("#scatterplot").append("div").style("left", "70px")
      .style("top", "30px").style("font-size", "10px").style("font-family", "Roboto").style("position", "absolute")
      .style("background-color", "white").style("border", "1px solid black").style("border-radius", "2px")
      .style("opacity", "0.75").style("padding", "5px");
    shapeLegend.append("div").text("Shape Legend");
    const shapeLegendPlus = shapeLegend.append("div");
    shapeLegendPlus.append("svg").style("width", "15px").style("height", "10px").append("path").attr("d", createPlusPolygon(5, 5));
    shapeLegendPlus.append("span").text("1 Bed");
    const shapeLegendCircle = shapeLegend.append("div");
    shapeLegendCircle.append("svg").style("width", "15px").style("height", "10px").append("circle").attr("cx", 5).attr("cy", 5).attr("r", 5);
    shapeLegendCircle.append("span").text("2 Beds");
    const shapeLegendTriangle = shapeLegend.append("div");
    shapeLegendTriangle.append("svg").style("width", "15px").style("height", "10px").append("path").attr("d", createTrianglePolygon(5, 5));
    shapeLegendTriangle.append("span").text("3 Beds");
    const shapeLegendRect = shapeLegend.append("div");
    shapeLegendRect.append("svg").style("width", "15px").style("height", "10px").append("rect").attr("x", "0").attr("y", "0").attr("width", "10").attr("height", "10");
    shapeLegendRect.append("span").text("4 Beds");
    const shapeLegendStar = shapeLegend.append("div");
    shapeLegendStar.append("svg").style("width", "15px").style("height", "10px").append("path").attr("d", createStarPolygon(5, 5));
    shapeLegendStar.append("span").text("5 Beds");
    //Regression Line Focus
    const focus = gBody.append("g").attr("class", "focus").style("display", "none");
    focus.append("line").classed("x", true);
    focus.append("line").classed("y", true);
    d3.selectAll(".grid line").style("stroke", "lightgrey").style("stroke-opacity", "0.7").style("shape-rendering", "crispEdges");
    focus.append("circle").attr("r", 5);
    d3.selectAll(".focus line").style('fill', 'none').style('stroke', 'black').style('stroke-width', '1.5px').style('stroke-dasharray', '3 3').style("pointer-events", "none");
    d3.selectAll(".focus circle").style("fill", "none").style("stroke", "black");
    let margin = { left: this.props.margin.marginLeft };
    // Check if there are more than 1 point, else the leastSquareCoeff and lineData are messed up
    if (xAxisData.length > 1) {
      gScatter.append("path").attr("stroke", "#b3b3b3").attr("stroke-width", 3)
        .attr("class", "line").attr("d", lineGenerator(lineData))
        .on("mouseover", function () {
          let x0 = x(x.invert(d3.mouse(this)[0]));
          let y0 = y(y.invert(d3.mouse(this)[1]));
          d3.select(".tooltip").style("left", d => `${d3.event.pageX + 8}px`).style("top", d => `${d3.event.pageY - 10}px`);
          d3.select(".tooltip").style("visibility", "visible");
          let priceTooltip = (leastSquareCoeff[3](x.invert(d3.event.pageX - svg.node().getBoundingClientRect().left - margin.left))).toLocaleString(undefined, { maximumFractionDigits: 2 });
          let sqftTooltip = (x.invert(d3.mouse(this)[0])).toLocaleString(undefined, { maximumFractionDigits: 1 });
          d3.select(".tooltip").text("Living Area: " + sqftTooltip + " sqft, Price: $" + priceTooltip);
          focus.attr('transform', `translate(${x0},${y0})`);
          focus.select("line.x").attr("x1", 0).attr("x2", -x0).attr("y1", 0).attr("y2", 0);
          focus.select("line.y").attr("x1", 0).attr("x2", 0).attr("y1", 0).attr("y2", height - y0);
          focus.style("display", null);
        })
        .on("mouseout", function () {
          d3.select(".tooltip").style("visibility", "hidden");
          focus.style('display', 'none');
        });
    }
    // Arbitrary Linear color gradient (blue -> yellow) based on price price
    let defs = gScatter.append("defs");
    let gradient = defs.append("linearGradient").attr("id", "svgGradient").attr("x1", "0%").attr("x2", "0%").attr("y1", "100%").attr("y2", "0%");
    // const scales = ["#440154","#450457","#46075a","#460a5d","#470d60","#471063","#471365","#481668","#48186a","#481b6d","#481d6f","#482071","#482374","#482576","#482878","#472a7a","#472d7b","#472f7d","#46327e","#463480","#453781","#443983","#443b84","#433e85","#424086","#414287","#404588","#3f4788","#3e4989","#3e4c8a","#3d4e8a","#3c508b","#3b528b","#3a548c","#39568c","#38598c","#375b8d","#365d8d","#355f8d","#34618d","#33638d","#32658e","#31678e","#30698e","#2f6b8e","#2e6d8e","#2e6f8e","#2d718e","#2c728e","#2b748e","#2a768e","#2a788e","#297a8e","#287c8e","#277e8e","#27808e","#26828e","#25838e","#25858e","#24878e","#23898e","#238a8d","#228b8d","#228c8d","#228d8d","#218e8d","#218f8d","#21908d","#21918c","#20928c","#20928c","#20938c","#1f948c","#1f958b","#1f968b","#1f978b","#1f988b","#1f998a","#1f9a8a","#1e9b8a","#1e9c89","#1e9d89","#1f9e89","#1f9f88","#1fa088","#1fa188","#1fa187","#1fa287","#20a386","#20a486","#21a585","#21a685","#22a785","#22a884","#23a983","#24aa83","#25ab82","#25ac82","#26ad81","#27ad81","#28ae80","#29af7f","#2ab07f","#2cb17e","#2db27d","#2eb37c","#2fb47c","#31b57b","#32b67a","#34b679","#35b779","#37b878","#38b977","#3aba76","#3bbb75","#3dbc74","#3fbc73","#40bd72","#42be71","#44bf70","#46c06f","#48c16e","#4ac16d","#4cc26c","#4ec36b","#50c46a","#52c569","#54c568","#56c667","#58c765","#5ac864","#5cc863","#5ec962","#60ca60","#63cb5f","#65cb5e","#67cc5c","#69cd5b","#6ccd5a","#6ece58","#70cf57","#73d056","#75d054","#77d153","#7ad151","#7cd250","#7fd34e","#81d34d","#84d44b","#86d549","#89d548","#8bd646","#8ed645","#90d743","#93d741","#95d840","#98d83e","#9bd93c","#9dd93b","#a0da39","#a2da37","#a5db36","#a8db34","#aadc32","#addc30","#b0dd2f","#b2dd2d","#b5de2b","#b8de29","#bade28","#bddf26","#c0df25","#c2df23","#c5e021","#c8e020","#cae11f","#cde11d","#d0e11c","#d2e21b","#d5e21a","#d8e219","#dae319","#dde318","#dfe318","#e2e418","#e5e419","#e7e419","#eae51a","#ece51b","#efe51c","#f1e51d","#f4e61e","#f6e620","#f8e621","#fbe723","#fde725"];
    const scales = ["#365c8d", "#20a386", "#3bbb75", "#fde725"];
    let colorRange = d3.range(0, 1, 1.0 / (scales.length - 1));
    colorRange.push(1);
    const colorScale = d3.scaleLinear().domain(colorRange).range(scales).interpolate(d3.interpolateHcl);
    const colorInterpolateScale = d3.scaleLinear().domain(d3.extent(yAxisData)).range([0, 1]);
    gradient.selectAll("stop").data(scales).enter().append("stop").attr("offset", function (d, i) { return i / (colorScale.range().length - 1); }).attr("stop-color", function (d) { return d; });

    //Plot points shapes
    // Based on beds, 1 bed = plus sign, 2 bed = circle, 3 bed = triangle, 4 bed = rectangle, 5 bed = star, anything else = circle
    circlePoints.map(function (d) {
      let shape;
      if (d.beds === 1) {
        shape = gScatter.append("path").attr("d", createPlusPolygon(d.x, d.y)).attr("class", "scatterPoint");
      } else if (d.beds === 2) {
        shape = gScatter.append("circle").attr("cx", d.x).attr("cy", d.y).attr("key", `${d.id},${d.x},${d.y}`).attr("r", 5).attr("class", "scatterPoint");
      } else if (d.beds === 3) {
        shape = gScatter.append("path").attr("d", createTrianglePolygon(d.x, d.y)).attr("class", "scatterPoint");
      } else if (d.beds === 4) {
        shape = gScatter.append("rect").attr("x", d.x - 4).attr("y", d.y - 4).attr("width", 8).attr("height", 8).attr("class", "scatterPoint");
      } else if (d.beds === 5) {
        shape = gScatter.append("path").attr("d", createStarPolygon(d.x, d.y)).attr("class", "scatterPoint");
      } else {
        shape = gScatter.append("circle").attr("cx", d.x).attr("cy", d.y).attr("key", `${d.id},${d.x},${d.y}`).attr("r", 5).attr("class", "scatterPoint");
      }
      shape.on("mouseover", () => {
        const compDetails = `${d.streetAddress}, ${d.city}, ${d.state}, ${d.zipcode} Price: $${(d.price)?.toLocaleString()} Sqft: ${(d.livingArea)?.toLocaleString()} Beds: ${d.beds}`;
        d3.select(".tooltip").style("left", () => `${d3.event.pageX + 8}px`).style("top", () => `${d3.event.pageY - 10}px`);
        d3.select(".tooltip").style("visibility", "visible");
        d3.select(".tooltip").text(compDetails);
      })
        .on("mouseout", function () {
          d3.select(".tooltip").style("visibility", "hidden");
          focus.style('display', 'none');
        });
      return shape;
    });
    //Color plot points
    svg.selectAll(".scatterPoint").style("stroke", "black").style("stroke-width", "0.33px").style("fill", function (d, i) { return colorScale(colorInterpolateScale(yAxisData[i])); });
    //Polygon shapes
    function createTrianglePolygon(xPosition, yPosition) {
      let path = `M${xPosition} ${yPosition + 3}
            L${xPosition + 5.196} ${yPosition + 3}
            L${xPosition + 2.598} ${yPosition - 1.5}
            L${xPosition} ${yPosition - 6}
            L${xPosition - 2.598} ${yPosition - 1.5}
            L${xPosition - 5.196} ${yPosition + 3}
            L${xPosition} ${yPosition + 3}`;
      return path;
    }
    function createPlusPolygon(xPosition, yPosition) {
      let path = `M${xPosition - 2} ${yPosition - 4} 
            h3 v3 
            h3 v3 
            h-3 v3 
            h-3 v-3 
            h-3 v-3 
            h3 z`;
      return path;
    }
    function createStarPolygon(xPosition, yPosition) {
      let path = `M${xPosition} ${yPosition + 3} 
            L${xPosition + 3.527} ${yPosition + 4.854} 
            L${xPosition + 2.853} ${yPosition + .927} 
            L${xPosition + 5.706} ${yPosition - 1.854} 
            L${xPosition + 1.763} ${yPosition - 2.427} 
            L${xPosition} ${yPosition - 6} 
            L${xPosition - 1.763} ${yPosition - 2.427} 
            L${xPosition - 5.706} ${yPosition - 1.854} 
            L${xPosition - 2.853} ${yPosition + .927} 
            L${xPosition - 3.527} ${yPosition + 4.854} 
            L${xPosition} ${yPosition + 3}`;
      return path;
    }

  }
  render() {
    return (
      <div id="scatterplot">
        {/* <Typography variant="headline">List Price vs Living Area</Typography> */}
      </div>
    )
  }
}

export default Scatterplot;